export const processActivities = (activities = []) => {
  if (!activities || activities.length === 0) {
    return {
      totalActivities: "0/0",
      latestActivity: "No Activity",
      drinkCount: 0,
      peeCount: 0,
      before10AM: "No",
      beforeNoon: "No",
      morningUrination: "No",
      hasDuplicates: 0,
      complianceStatus: "no-data",
      complianceIssues: ["No activity data"],
    };
  }

  // Get today's date in the user's timezone
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split("T")[0];

  // Generate report for today
  const report = generateReport({ activities }, todayStr);

  // Get latest activity time
  let latestActivity = null;
  const activitiesWithTime = activities
    .filter((a) => a.aTime?.S)
    .sort((a, b) => new Date(b.aTime.S) - new Date(a.aTime.S));

  if (activitiesWithTime.length > 0) {
    latestActivity = activitiesWithTime[0].aTime.S;
  }

  // Determine compliance status based on report
  const issues = [];
  let isCompliant = true;

  // Check compliance rules
  if (report.missing4HourGap === "No Activity Found") {
    issues.push("Missing diary entries (4+ hour gap)");
    isCompliant = false;
  }
  if (report.missing6HourGap === "No Activity Found") {
    issues.push("Missing void entries (6+ hour gap)");
    isCompliant = false;
  }
  if (report.morningUrination === "No Activity Found") {
    issues.push("No urine output within 2 hours of waking");
    isCompliant = false;
  }
  if (report.duplicateEntries > 0) {
    issues.push(`Found ${report.duplicateEntries} duplicate entries`);
    isCompliant = false;
  }

  return {
    totalActivities: `${report.totalActivityCount}/${report.totalActivities}`,
    latestActivity: latestActivity
      ? new Date(latestActivity).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "No Activity",
    drinkCount: report.drinkCount,
    peeCount: report.peeCount,
    before10AM: report.missing4HourGap === "Activity Found" ? "Yes" : "No",
    beforeNoon: report.missing6HourGap === "Activity Found" ? "Yes" : "No",
    morningUrination:
      report.morningUrination === "Activity Found" ? "Yes" : "No",
    hasDuplicates: report.duplicateEntries,
    complianceStatus: isCompliant ? "compliant" : "non-compliant",
    complianceIssues: issues.length > 0 ? issues : ["No compliance issues"],
  };
};
export const generateReport = (userData, reportDateStr) => {
  const activities = userData?.activities || [];
  const email = userData?.email || "";
  const studyId = userData?.study_id || "";
  const studyName = userData?.studyName || "";
  const reportDateISO = new Date(reportDateStr).toISOString().slice(0, 10);

  const dayActivities = activities
    .filter((a) => {
      const t = a.aTime?.S;
      if (!t) return false;
      const actDate = new Date(t).toISOString().slice(0, 10);
      return actDate === reportDateISO;
    })
    .sort((a, b) => new Date(a.aTime.S) - new Date(b.aTime.S));

  let drinkCount = 0,
    peeCount = 0,
    leakCount = 0,
    urgencyCount = 0,
    duplicateEntries = 0,
    morningUrination = "No Activity Found",
    totalActivities = activities.length;

  const timePoints = [];

  for (const act of dayActivities) {
    const type = act.type?.S || "";
    const time = new Date(act.aTime?.S);
    timePoints.push(time);

    if (type === "Intake") drinkCount++;
    if (type === "Output") {
      peeCount++;
      try {
        const q = JSON.parse(act.questionnaire?.S || "{}");
        if (q.ql) leakCount++;
        if (q.qutv) urgencyCount++;
      } catch (e) {
        console.error("Error parsing questionnaire:", e);
      }

      const wakeStart = new Date(`${reportDateISO}T14:00:00Z`).getTime();
      const wakeEnd = new Date(`${reportDateISO}T16:00:00Z`).getTime();
      const actTime = time.getTime();
      if (actTime >= wakeStart && actTime <= wakeEnd) {
        morningUrination = "Activity Found";
      }
    }
  }

  const checkedIndexes = new Set();
  for (let i = 0; i < dayActivities.length; i++) {
    if (checkedIndexes.has(i)) continue;

    const a1 = dayActivities[i];
    const t1 = new Date(a1.aTime?.S).getTime();

    for (let j = i + 1; j < dayActivities.length; j++) {
      if (checkedIndexes.has(j)) continue;

      const a2 = dayActivities[j];
      const t2 = new Date(a2.aTime?.S).getTime();
      const timeDiff = Math.abs(t1 - t2);

      if (timeDiff <= 5 * 60 * 1000 && areActivitiesEqual(a1, a2)) {
        duplicateEntries++;
        checkedIndexes.add(j);
        break;
      }
    }
  }

  // Helper function to compare activities
  function areActivitiesEqual(a1, a2) {
    const omitKeys = ["id", "aTime"];
    const keys1 = Object.keys(a1).filter((k) => !omitKeys.includes(k));
    const keys2 = Object.keys(a2).filter((k) => !omitKeys.includes(k));

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      const v1 = JSON.stringify(a1[key]);
      const v2 = JSON.stringify(a2[key]);
      if (v1 !== v2) return false;
    }

    return true;
  }

  const start6AM = new Date(`${reportDateISO}T06:00:00Z`).getTime();
  const end10AM = new Date(`${reportDateISO}T10:00:00Z`).getTime();
  const end12PM = new Date(`${reportDateISO}T12:00:00Z`).getTime();

  const hasAnyActivity6to10 = dayActivities.some((a) => {
    const t = new Date(a.aTime?.S).getTime();
    return t >= start6AM && t < end10AM;
  });

  const hasPee6to12 = dayActivities.some((a) => {
    const t = new Date(a.aTime?.S).getTime();
    const type = a.type?.S;
    return type === "Output" && t >= start6AM && t < end12PM;
  });

  const missing4HourGap = hasAnyActivity6to10
    ? "Activity Found"
    : "No Activity Found";
  const missing6HourGap = hasPee6to12 ? "Activity Found" : "No Activity Found";

  const latestActivity = timePoints.length
    ? new Date(Math.max(...timePoints.map((d) => d.getTime())))
    : null;

  return {
    patientEmailId: email,
    studyId,
    studyName,
    totalActivityCount: dayActivities.length,
    totalActivities,
    date: latestActivity
      ? latestActivity.toISOString().replace("T", " ").slice(0, 19)
      : "No Activity",
    drinkCount,
    peeCount,
    leakCount,
    urgencyCount,
    missing4HourGap,
    missing6HourGap,
    duplicateEntries,
    morningUrination,
  };
};
export const RUN_RESULT_OPTIONS = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "pending", label: "Pending" },
];

export const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "completed", label: "Completed" },
];

export const getOptionLabel = (options, value, fallback = "—") => {
  const normalizedValue = String(value ?? "").toLowerCase();
  return (
    options.find((option) => option.value.toLowerCase() === normalizedValue)
      ?.label ?? fallback
  );
};

export const getStatusStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "completed":
      return {
        backgroundColor: "#b2f8d66f",
        color: "#2ca56bff",
        border: "1px solid #33bc7aff",
      };
    case "inactive":
      return {
        backgroundColor: "#f0f0f0",
        color: "#737374ff",
        border: "1px solid #7b7c7ddb",
      };
    case "active":
      return {
        backgroundColor: "#b2daf94f",
        color: "#056cbc",
        border: "1px solid #056cbc",
      };
    default:
      return {
        backgroundColor: "#f0f0f0",
        color: "#333",
        border: "1px solid #33333325",
      };
  }
};

export const getRunResultStyle = (run_result) => {
  switch ((run_result || "").toLowerCase()) {
    case "pass":
      return {
        backgroundColor: "#b2f8d66f",
        color: "#2ca56bff",
        border: "1px solid #33bc7aff",
      };
    case "fail":
      return {
        backgroundColor: "#fbdddd6e",
        color: "#c74747ff",
        border: "1px solid #c74747ff",
      };
    case "pending":
      return {
        backgroundColor: "#fde8c242",
        color: "#f2a414ff",
        border: "1px solid #f4be59ff",
      };
    default:
      return {
        backgroundColor: "#f0f0f0",
        color: "#333",
        border: "1px solid #33333325",
      };
  }
};